/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useCallback, useRef, useState } from 'react';
import type { UseMutationResult } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { extractErrorMessage } from '../utils/errors';
import { describeSsoError, type ResolvedSsoError } from '../utils/ssoErrors';

export interface RunMutationConfig<TData> {
  fallbackMessage: string;
  onSuccess?: (data: TData) => void | Promise<void>;
  onError?: (message: string) => void;
}

export function useMutationErrorState(
  externalOnError?: (message: string) => void,
) {
  const { t } = useTranslation('account');
  const [error, setErrorState] = useState<ResolvedSsoError | undefined>();
  const externalOnErrorRef = useRef(externalOnError);
  externalOnErrorRef.current = externalOnError;

  const clearError = useCallback(() => setErrorState(undefined), []);

  const fail = useCallback((message: string) => {
    setErrorState({ message });
    externalOnErrorRef.current?.(message);
  }, []);

  // Keeps the SSO code so OTPView can show OTP_VERIFICATION_FAIL inline.
  const setError = useCallback(
    (rawError: unknown, fallbackMessage: string): string => {
      const resolved = describeSsoError(
        rawError,
        t,
        extractErrorMessage(rawError, fallbackMessage),
      );
      setErrorState(resolved);
      externalOnErrorRef.current?.(resolved.message);
      return resolved.message;
    },
    [t],
  );

  const run = useCallback(
    <TData, TVars>(
      mutation: UseMutationResult<TData, unknown, TVars>,
      vars: TVars,
      { fallbackMessage, onSuccess, onError }: RunMutationConfig<TData>,
    ) => {
      setErrorState(undefined);
      mutation.mutate(vars, {
        onSuccess: (data) => {
          void onSuccess?.(data);
        },
        onError: (error) => {
          const message = setError(error, fallbackMessage);
          onError?.(message);
        },
      });
    },
    [setError],
  );

  const runAsync = useCallback(
    async <TData, TVars>(
      mutation: UseMutationResult<TData, unknown, TVars>,
      vars: TVars,
      { fallbackMessage, onSuccess, onError }: RunMutationConfig<TData>,
    ) => {
      setErrorState(undefined);
      try {
        const data = await mutation.mutateAsync(vars);
        await onSuccess?.(data);
      } catch (error) {
        const message = setError(error, fallbackMessage);
        onError?.(message);
      }
    },
    [setError],
  );

  return {
    errorMessage: error?.message,
    errorCode: error?.code,
    clearError,
    fail,
    run,
    runAsync,
  };
}
